import React, { FC, useEffect, useState } from 'react';
import { Button, IconButton, Paper, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { IAbsenceRequestParams } from 'src/api/dtos';
import { AbsenceDetailsDrawer, AbsenceRequestDrawer, ScheduledAbsences } from 'src/features/absences';
import {
  ClockDottedIcon,
  EmptyState,
  InfoFilledIcon,
  ServerError500Icon,
  Tooltip,
  UserXIcon,
} from 'src/shared/components';
import { FeaturesEnums, SortDirectionEnums } from 'src/shared/enums';
import { ComponentWithFeatures } from 'src/shared/hocs';
import { useFeatures, useThunkDispatch } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import {
  fetchAbsences,
  fetchAbsenceStatuses,
  fetchAbsenceTypes,
  fetchApprovers,
  fetchScheduledAbsences,
  cancelAbsence,
} from 'src/store/absence/absences.actions';
import { RootState } from 'src/store/types';

export const AbsencesCard: FC = () => {
  const { t } = useTranslate();
  const dispatch = useThunkDispatch();
  const { approvers, scheduled, loadingData, loadingScheduled, submitting, error } = useSelector(
    (state: RootState) => state.absences,
  );
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [openRequest, setOpenRequest] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<IAbsenceRequestParams>({});
  const hasScheduledAbsencesFeature = useFeatures(FeaturesEnums.Schedule_Employee_Absences);
  const hasAbsenceRequestFeature = useFeatures(FeaturesEnums.AbsenceRequest);

  useEffect(() => {
    if (!hasScheduledAbsencesFeature) {
      return;
    }

    const fetchData = async () => {
      try {
        await dispatch(fetchScheduledAbsences({ sortDirection: SortDirectionEnums.ASCENDING }));
      } catch {
        toast.info(t('absences.oopsAnErrorOccurredWhileGettingYourListOfScheduledAbsences'), {
          type: toast.TYPE.ERROR,
        });
      }
    };

    fetchData();
  }, [hasScheduledAbsencesFeature]);

  useEffect(() => {
    if (!hasAbsenceRequestFeature) {
      return;
    }

    dispatch(fetchAbsences());
    dispatch(fetchAbsenceStatuses());
    dispatch(fetchAbsenceTypes());

    if (!approvers.length) {
      dispatch(fetchApprovers());
    }
  }, [hasAbsenceRequestFeature]);

  const handleFilterChange = async (queryParams: IAbsenceRequestParams) => {
    try {
      await dispatch(fetchAbsences(queryParams));
      setCurrentFilters({ ...queryParams });
    } catch {
      toast.info(t('absences.oopsAnErrorOccurredWhileGettingYourApprovers'), { type: toast.TYPE.ERROR });
    }
  };

  const handleAbsenceStatusUpdate = async (id: number) => {
    try {
      await dispatch(cancelAbsence({ notificationIds: [id], params: currentFilters }));
    } catch {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), { type: toast.TYPE.ERROR });
    }
  };

  const handleOpenDetailsDrawer = () => {
    setOpenDetails(true);
    dispatch(fetchAbsences());
  };

  if ((!openDetails && loadingData) || loadingScheduled) {
    return <Skeleton className="rounded border" variant="rectangular" animation="wave" height={320} />;
  }

  if (error) {
    return (
      <Paper className="h-100 p-4">
        <h4 className="pb-0 mb-0">{t('absences.upcomingAbsences')}</h4>
        <EmptyState className="m-0" message={t('global.oopsAnErrorOccurred')} icon={<ServerError500Icon />} />
      </Paper>
    );
  }

  return (
    <>
      <Paper id="absences-card" className="h-100 p-4 overflow-hidden">
        <div className="d-flex flex-wrap justify-content-between align-items-center pb-2">
          <div className="d-flex align-items-center">
            <h4 className="pb-0 mb-0 me-1">{t('absences.upcomingAbsences')}</h4>
            <Tooltip placement="top" title={t('absences.shownBelowAreYourUpcomingAbsences')}>
              <IconButton className="p-0" size="small" color="info">
                <InfoFilledIcon size={16} />
              </IconButton>
            </Tooltip>
          </div>
          <ComponentWithFeatures features={FeaturesEnums.AbsenceRequest}>
            <div className="d-flex flex-wrap">
              <Button
                id="absence-details"
                className="me-2 mb-2 mb-sm-0"
                color="secondary"
                onClick={handleOpenDetailsDrawer}
                startIcon={<ClockDottedIcon />}
              >
                {t('absences.absenceDetails')}
              </Button>
              <Tooltip title={!approvers.length ? t('absences.sorryYouNeedApproversToRequestAnAbsence') : ''}>
                <span>
                  <Button
                    id="request-an-absence"
                    className="mb-2 mb-sm-0"
                    variant="contained"
                    onClick={() => setOpenRequest(true)}
                    startIcon={<UserXIcon />}
                    disabled={!approvers.length}
                  >
                    {t('absences.requestAnAbsence')}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </ComponentWithFeatures>
        </div>
        <ScheduledAbsences scheduledAbsences={scheduled} />
      </Paper>
      {!!approvers.length && (
        <AbsenceRequestDrawer
          open={openRequest}
          cancelling={submitting}
          onClose={() => setOpenRequest(false)}
          onCancelling={handleAbsenceStatusUpdate}
          onViewDetails={() => {
            setOpenRequest(false);
            setOpenDetails(true);
          }}
        />
      )}
      <AbsenceDetailsDrawer
        open={openDetails}
        cancelling={submitting}
        onClose={() => setOpenDetails(false)}
        onCancelling={handleAbsenceStatusUpdate}
        onFiltersChange={handleFilterChange}
      />
    </>
  );
};
